import React, { useEffect, useRef } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RoomEnvironment } from "three/examples/jsm/environments/RoomEnvironment.js";

const BOARD_MODEL_URL = "/design-preview/mirror-board.glb";
const PLAYER_COLOR_ANIMATIONS = new Set([
  "smart-cube",
  "locked-up-cubes",
  "rounded-cube",
  "reflective-cube",
  "companion-cube",
]);
const PREVIEW_PLAYER_COLORS = [
  "#005bff", // Player 1
  "#ff003d", // Player 2
  "#ffea00", // Player 3
  "#04ff17", // Player 4
  "#8b5cf6",
  "#00cfe8",
  "#ff7a00",
  "#e600b8",
  "#7ee000",
].map((color) => new THREE.Color(color));
const MIXER_MODEL_ANIMATIONS = new Set([
  "animated-rubiks-cube",
  "gear-cube",
]);
const PROCEDURAL_MODEL_ANIMATIONS = new Set([
  "liquid-glass-cube",
  "voxel-core-cube",
]);
const GEAR_MODEL_SCALE = 1.936;
const GEAR_ANIMATION_SPEED = 0.55;
const GEAR_SQUARED_HOLD_SECONDS = 3;
const DEFAULT_MODEL_SCALE = 0.88;
const BLOCK_POSITIONS = [
  [-5.25, 1.02, 3.15],
  [-2.15, 1.02, -5.25],
  [4.15, 1.02, 5.2],
  [5.2, 1.02, -1.15],
  [-3.25, 3.02, 2.15],
  [3.2, 3.02, -3.15],
  [1.25, 3.02, 3.25],
  [-1.25, 5.02, 1.25],
  [1.25, 5.02, -1.25],
];
const SPECIAL_POSITION = [5.2, 1.04, 2.1];

const resolveBlockTextureRepeat = (textureId) => {
  switch (String(textureId || "").trim().toLowerCase()) {
    case "paving-stones-138": return [1.44, 0.72];
    case "aged-brick": return [3.3, 3.3];
    case "paving-stones-076": return [1.75, 1.75];
    case "tiles-031": return [1.55, 1.55];
    case "tiles-100": return [1.45, 1.45];
    case "wood-floor-035": return [1.65, 1.2];
    default: return [1.35, 1.35];
  }
};

const normalizeMaterialName = (value) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const setMaterialStyle = (material, style = {}) => {
  if (!material || !style) return;
  if (style.color && material.color) material.color.set(style.color);
  if (Number.isFinite(style.metalness) && "metalness" in material) material.metalness = style.metalness;
  if (Number.isFinite(style.roughness) && "roughness" in material) material.roughness = style.roughness;
  if (style.emissive && material.emissive) {
    material.emissive.set(style.emissive);
    material.emissiveIntensity = Number.isFinite(style.emissiveIntensity) ? style.emissiveIntensity : 1;
  }
  if (Number.isFinite(style.opacity)) {
    material.opacity = style.opacity;
    material.transparent = style.opacity < 1;
    material.depthWrite = style.opacity >= 1;
    material.side = THREE.DoubleSide;
  }
};

const resolveAuthoredMaterialStyle = (animationId, rawMaterialName) => {
  const name = normalizeMaterialName(rawMaterialName);
  const exact = (entries) => entries[name] || null;

  switch (animationId) {
    case "smart-cube":
      return { metalness: 0.24, roughness: 0.32 };
    case "locked-up-cubes":
      return { metalness: 0.34, roughness: 0.38 };
    case "rounded-cube":
      return { metalness: 0.12, roughness: 0.3 };
    case "reflective-cube":
      return name.includes("glass")
        ? { color: "#c8f7ff", metalness: 0.04, roughness: 0.08, opacity: 0.22 }
        : { metalness: 0.68, roughness: 0.43 };
    case "companion-cube":
      return name.includes("glow")
        ? { color: "#fff4f8", metalness: 0.05, roughness: 0.45, emissive: "#ff9fca", emissiveIntensity: 1.2 }
        : { metalness: 0.18, roughness: 0.6 };
    case "puzzle-cube":
      return exact({
        blue: { color: "#2e80e8", metalness: 0.08, roughness: 0.38 },
        green: { color: "#66a82b", metalness: 0.08, roughness: 0.38 },
        magenta: { color: "#eb40db", metalness: 0.08, roughness: 0.38 },
        orange: { color: "#ff9933", metalness: 0.08, roughness: 0.38 },
        red: { color: "#ed403b", metalness: 0.08, roughness: 0.38 },
        yellow: { color: "#ffd133", metalness: 0.08, roughness: 0.38 },
        seam: { color: "#0b0b0e", metalness: 0.08, roughness: 0.38 },
      });
    case "animated-rubiks-cube":
      return exact({
        "material.001": { color: "#020305", metalness: 0.12, roughness: 0.3 },
        "material.002": { color: "#03b829", metalness: 0.04, roughness: 0.48 },
        "material.003": { color: "#ebf5ff", metalness: 0.04, roughness: 0.48 },
        "material.004": { color: "#eb0506", metalness: 0.04, roughness: 0.48 },
        "material.005": { color: "#0429f2", metalness: 0.04, roughness: 0.48 },
        "material.006": { color: "#ffd103", metalness: 0.04, roughness: 0.48 },
        "material.007": { color: "#ff4003", metalness: 0.04, roughness: 0.48 },
      });
    case "puzzle-cube-jigsaw":
      return exact({
        pink: { color: "#d31a96", metalness: 0.22, roughness: 0.52 },
        purple: { color: "#a025d5", metalness: 0.22, roughness: 0.52 },
        red: { color: "#ba3530", metalness: 0.22, roughness: 0.52 },
        brown: { color: "#845e42", metalness: 0.22, roughness: 0.52 },
        orange: { color: "#d5671f", metalness: 0.22, roughness: 0.52 },
        blue: { color: "#2863d7", metalness: 0.22, roughness: 0.52 },
        yellow: { color: "#d1b633", metalness: 0.22, roughness: 0.52 },
        green: { color: "#396e21", metalness: 0.22, roughness: 0.52 },
        core: { color: "#242528", metalness: 0.12, roughness: 0.52 },
      });
    case "abstract-spinning-cube": {
      if (name.includes("outline")) return { color: "#010205", metalness: 0.76, roughness: 0.64 };
      if (name.includes("white transparent")) return { color: "#b8f5ff", metalness: 0.08, roughness: 0.1, opacity: 0.22 };
      return exact({
        yellow: { color: "#ffd904", emissive: "#ffd904", emissiveIntensity: 1.4 },
        green: { color: "#05ff3d", emissive: "#05ff3d", emissiveIntensity: 1.4 },
        orange: { color: "#ff5704", emissive: "#ff5704", emissiveIntensity: 1.4 },
        red: { color: "#ff0608", emissive: "#ff0608", emissiveIntensity: 1.4 },
        cyan: { color: "#03f2ff", emissive: "#03f2ff", emissiveIntensity: 1.4 },
        blue: { color: "#0847ff", emissive: "#0847ff", emissiveIntensity: 1.4 },
        pink: { color: "#ff05a3", emissive: "#ff05a3", emissiveIntensity: 1.4 },
        purple: { color: "#9408ff", emissive: "#9408ff", emissiveIntensity: 1.4 },
      });
    }
    case "classic-rubik-cube":
      return exact({
        rubikred: { color: "#eb0506", metalness: 0.04, roughness: 0.52 },
        rubikorange: { color: "#ff4003", metalness: 0.04, roughness: 0.52 },
        rubikblue: { color: "#0429f2", metalness: 0.04, roughness: 0.52 },
        rubikgreen: { color: "#03b829", metalness: 0.04, roughness: 0.52 },
        rubikwhite: { color: "#ebf5ff", metalness: 0.04, roughness: 0.52 },
        rubikyellow: { color: "#ffd103", metalness: 0.04, roughness: 0.52 },
        rubikblack: { color: "#020305", metalness: 0.12, roughness: 0.7 },
      });
    case "mirror-rubik-silver":
      return name.includes("sticker")
        ? { color: "#cbd4dd", metalness: 0.88, roughness: 0.22 }
        : { color: "#06080b", metalness: 0.38, roughness: 0.6 };
    case "mirror-rubik-gold":
      return name.includes("sticker")
        ? { color: "#e4a51c", metalness: 0.86, roughness: 0.25 }
        : { color: "#090603", metalness: 0.38, roughness: 0.6 };
    case "burning-crystal-cube":
      return { color: "#2e0301", metalness: 0.34, roughness: 0.3, emissive: "#ff2404", emissiveIntensity: 2.8 };
    case "gear-cube":
      return exact({
        mtl: { color: "#708599", metalness: 0.92, roughness: 0.22 },
        mtl1: { color: "#b8610e", metalness: 0.9, roughness: 0.3 },
        mtl2: { color: "#051a21", metalness: 0.78, roughness: 0.28, emissive: "#05b8eb", emissiveIntensity: 1.15 },
      });
    case "voxel-core-cube":
      if (name.startsWith("voxelcore")) return { color: "#38f279", metalness: 0.05, roughness: 0.22, emissive: "#26ff6b", emissiveIntensity: 2.5 };
      if (name.startsWith("voxelmidnight")) return { color: "#05070f", metalness: 0.68, roughness: 0.42 };
      if (name.startsWith("voxelteal")) return { color: "#05524d", metalness: 0.52, roughness: 0.34 };
      if (name.startsWith("voxelpurple")) return { color: "#4f137a", metalness: 0.44, roughness: 0.38 };
      if (name.startsWith("voxelblack")) return { color: "#010203", metalness: 0.72, roughness: 0.36 };
      return null;
    case "water-tank":
      return exact({
        tank: { color: "#08addb", metalness: 0.08, roughness: 0.08, opacity: 0.34 },
        metal: { color: "#576b7a", metalness: 0.88, roughness: 0.28 },
        red: { color: "#b8090c", metalness: 0.38, roughness: 0.38 },
        white: { color: "#c7dbe6", metalness: 0.42, roughness: 0.28 },
        black: { color: "#020305", metalness: 0.7, roughness: 0.5 },
      });
    case "neon-cage-cube":
      return name.includes("teal")
        ? { color: "#045951", metalness: 0.7, roughness: 0.28, emissive: "#08d1b8", emissiveIntensity: 0.8 }
        : { color: "#38065c", metalness: 0.62, roughness: 0.32, emissive: "#b314ff", emissiveIntensity: 0.75 };
    case "impossible-cube":
      return name.startsWith("01")
        ? { color: "#01090b", metalness: 0.68, roughness: 0.46 }
        : { color: "#336104", metalness: 0.38, roughness: 0.24, emissive: "#8fff05", emissiveIntensity: 1.65 };
    case "one-line-cube":
      return { color: "#02131f", metalness: 0.6, roughness: 0.22, emissive: "#05b8ff", emissiveIntensity: 1.45 };
    case "liquid-glass-cube":
      return { color: "#0694eb", metalness: 0.08, roughness: 0.06, opacity: 0.52, emissive: "#032e57", emissiveIntensity: 0.72 };
    case "energy-crate":
      return { color: "#4d0201", metalness: 0.72, roughness: 0.42, emissive: "#14ff57", emissiveIntensity: 0.35 };
    case "sci-fi-crate":
      return { color: "#24313f", metalness: 0.74, roughness: 0.38 };
    case "sci-fi-cube":
      return { color: "#087f91", metalness: 1, roughness: 0.35, emissive: "#00d5ff", emissiveIntensity: 0.32 };
    default:
      return null;
  }
};

const createCompanionTintTexture = (sourceTexture, playerColor, textureCache, ownedTextures) => {
  const sourceImage = sourceTexture?.image;
  const width = sourceImage?.width || sourceImage?.videoWidth || 0;
  const height = sourceImage?.height || sourceImage?.videoHeight || 0;
  if (!sourceTexture || !sourceImage || !width || !height || typeof document === "undefined") return null;

  const cacheKey = `${sourceTexture.uuid}:${playerColor.getHexString()}`;
  if (textureCache.has(cacheKey)) return textureCache.get(cacheKey);
  try {
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    context.drawImage(sourceImage, 0, 0, width, height);
    const imageData = context.getImageData(0, 0, width, height);
    const target = playerColor.clone().convertLinearToSRGB();
    for (let offset = 0; offset < imageData.data.length; offset += 4) {
      const red = imageData.data[offset];
      const green = imageData.data[offset + 1];
      const blue = imageData.data[offset + 2];
      const maximum = Math.max(red, green, blue);
      const minimum = Math.min(red, green, blue);
      if (minimum < 190 || maximum - minimum > 24) continue;
      const brightness = (red + green + blue) / (3 * 255);
      imageData.data[offset] = Math.round(target.r * brightness * 255);
      imageData.data[offset + 1] = Math.round(target.g * brightness * 255);
      imageData.data[offset + 2] = Math.round(target.b * brightness * 255);
    }
    context.putImageData(imageData, 0, 0);
    const tinted = new THREE.CanvasTexture(canvas);
    tinted.colorSpace = THREE.SRGBColorSpace;
    tinted.flipY = sourceTexture.flipY;
    tinted.wrapS = sourceTexture.wrapS;
    tinted.wrapT = sourceTexture.wrapT;
    tinted.magFilter = sourceTexture.magFilter;
    tinted.minFilter = sourceTexture.minFilter;
    tinted.anisotropy = sourceTexture.anisotropy;
    tinted.needsUpdate = true;
    textureCache.set(cacheKey, tinted);
    ownedTextures.push(tinted);
    return tinted;
  } catch (_) {
    return null;
  }
};

// Mirrors Unity's FlagWallMeshBuilder: every vertical wall face gets upright
// UVs with whole 3:2 flags from left to right, one flag tall.
const FLAG_WALL_ASPECT = 1.5;
const applyFlagWallUvs = (mesh) => {
  const geometry = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry.clone();
  const position = geometry.getAttribute("position");
  if (!position) return;
  mesh.updateMatrixWorld(true);
  const up = new THREE.Vector3(0, 1, 0);
  const world = Array.from({ length: position.count }, (_, index) =>
    new THREE.Vector3().fromBufferAttribute(position, index).applyMatrix4(mesh.matrixWorld),
  );
  const shadingNormals = geometry.getAttribute("normal");
  const normalMatrix = new THREE.Matrix3().getNormalMatrix(mesh.matrixWorld);
  const uvs = new Float32Array(position.count * 2);
  const faces = new Map();
  for (let index = 0; index + 2 < position.count; index += 3) {
    const normal = new THREE.Vector3()
      .subVectors(world[index + 1], world[index])
      .cross(new THREE.Vector3().subVectors(world[index + 2], world[index]))
      .normalize();
    if (shadingNormals) {
      const shading = new THREE.Vector3();
      for (let k = 0; k < 3; k += 1) shading.add(new THREE.Vector3().fromBufferAttribute(shadingNormals, index + k));
      if (shading.applyMatrix3(normalMatrix).dot(normal) < 0) normal.negate();
    }
    if (!Number.isFinite(normal.x) || Math.abs(normal.y) > 0.35) continue;
    normal.y = 0;
    normal.normalize();
    const key = [
      Math.round(normal.x * 4),
      Math.round(normal.z * 4),
      Math.round(world[index].dot(normal) * 50),
    ].join(":");
    if (!faces.has(key)) faces.set(key, { normal, triangles: [] });
    faces.get(key).triangles.push(index);
  }
  faces.forEach(({ normal, triangles }) => {
    const right = new THREE.Vector3().crossVectors(up, normal).normalize();
    let minR = Infinity;
    let maxR = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;
    triangles.forEach((start) => {
      for (let k = 0; k < 3; k += 1) {
        const point = world[start + k];
        const r = point.dot(right);
        minR = Math.min(minR, r);
        maxR = Math.max(maxR, r);
        minY = Math.min(minY, point.y);
        maxY = Math.max(maxY, point.y);
      }
    });
    const height = Math.max(0.0001, maxY - minY);
    const length = Math.max(0.0001, maxR - minR);
    const naturalWidth = FLAG_WALL_ASPECT * height;
    const flagCount = Math.round(length / naturalWidth);
    const centerR = (minR + maxR) / 2;
    triangles.forEach((start) => {
      for (let k = 0; k < 3; k += 1) {
        const point = world[start + k];
        const r = point.dot(right);
        uvs[(start + k) * 2] = flagCount >= 1
          ? ((r - minR) / length) * flagCount
          : 0.5 + (r - centerR) / naturalWidth;
        uvs[(start + k) * 2 + 1] = 0.004 + 0.992 * ((point.y - minY) / height);
      }
    });
  });
  geometry.setAttribute("uv", new THREE.BufferAttribute(uvs, 2));
  mesh.geometry = geometry;
};

const loadTexture = (loader, source, { colorSpace = true, repeat = 1 } = {}) =>
  new Promise((resolve) => {
    const url = String(source || "").trim();
    if (!url) {
      resolve(null);
      return;
    }
    loader.load(
      url,
      (texture) => {
        if (colorSpace) texture.colorSpace = THREE.SRGBColorSpace;
        texture.wrapS = THREE.RepeatWrapping;
        texture.wrapT = THREE.RepeatWrapping;
        const repeatX = Array.isArray(repeat) ? Number(repeat[0] || 1) : Number(repeat || 1);
        const repeatY = Array.isArray(repeat) ? Number(repeat[1] || repeatX) : repeatX;
        texture.repeat.set(repeatX, repeatY);
        texture.anisotropy = 8;
        texture.needsUpdate = true;
        resolve(texture);
      },
      undefined,
      () => resolve(null),
    );
  });

const makeMaterialSet = ({ color, texture, normalMap, roughnessMap, metalnessMap, special }) =>
  Array.from({ length: 6 }, () =>
    new THREE.MeshStandardMaterial({
      color,
      map: texture,
      normalMap,
      roughnessMap,
      metalnessMap,
      metalness: special ? 0.12 : metalnessMap ? 0.48 : 0.18,
      roughness: special ? 0.26 : roughnessMap ? 0.52 : 0.34,
    }),
  );

const addPreviewCube = ({
  scene,
  position,
  size = 0.88,
  color,
  texture,
  normalMap,
  roughnessMap,
  metalnessMap,
  special = false,
}) => {
  const geometry = new THREE.BoxGeometry(size, size, size, 2, 2, 2);
  const materials = makeMaterialSet({
    color,
    texture,
    normalMap,
    roughnessMap,
    metalnessMap,
    special,
  });
  const cube = new THREE.Mesh(geometry, materials);
  cube.position.set(...position);
  cube.castShadow = true;
  cube.receiveShadow = true;
  scene.add(cube);
  return cube;
};

const addSpecialMarkers = (scene, position, size, texture, ownedMaterials) => {
  const markerSize = size * 0.7;
  const offset = size * 0.505;
  const faces = [
    { offset: [0, 0, offset], rotation: [0, 0, 0] },
    { offset: [0, 0, -offset], rotation: [0, Math.PI, 0] },
    { offset: [offset, 0, 0], rotation: [0, Math.PI / 2, 0] },
    { offset: [-offset, 0, 0], rotation: [0, -Math.PI / 2, 0] },
    { offset: [0, offset, 0], rotation: [-Math.PI / 2, 0, 0] },
    { offset: [0, -offset, 0], rotation: [Math.PI / 2, 0, 0] },
  ];
  const markers = [];
  faces.forEach((face, index) => {
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      color: texture ? 0xffffff : 0xffdd45,
      transparent: true,
      alphaTest: 0.02,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -3,
      side: THREE.DoubleSide,
    });
    ownedMaterials.push(material);
    const marker = new THREE.Mesh(new THREE.PlaneGeometry(markerSize, markerSize), material);
    marker.name = `Bloque especial cara ${index + 1}`;
    marker.position.set(
      position[0] + face.offset[0],
      position[1] + face.offset[1],
      position[2] + face.offset[2],
    );
    marker.rotation.set(...face.rotation);
    marker.renderOrder = 8;
    scene.add(marker);
    markers.push(marker);
  });
  return markers;
};

const cloneAnimationModel = (
  source,
  position,
  animationId,
  ownedMaterials,
  instanceIndex,
  companionTextureCache,
  ownedTextures,
) => {
  const clone = source.clone(true);
  clone.position.set(...position);
  clone.scale.multiplyScalar(animationId === "gear-cube" ? GEAR_MODEL_SCALE : DEFAULT_MODEL_SCALE);
  clone.name = `${animationId} preview`;
  clone.traverse((child) => {
    if (!child.isMesh) return;
    child.castShadow = true;
    child.receiveShadow = true;
    const sourceMaterials = Array.isArray(child.material) ? child.material : [child.material];
    const materials = sourceMaterials.map((material) => {
      const next = material.clone();
      const normalizedName = normalizeMaterialName(next.name);
      setMaterialStyle(next, resolveAuthoredMaterialStyle(animationId, next.name));
      if (PLAYER_COLOR_ANIMATIONS.has(animationId)) {
        const playerColor = PREVIEW_PLAYER_COLORS[instanceIndex % PREVIEW_PLAYER_COLORS.length];
        const shouldTint = animationId === "reflective-cube"
          ? normalizedName.includes("shell")
          : animationId === "companion-cube"
            ? !normalizedName.includes("glow")
            : true;
        if (shouldTint) {
          // Player colors are identity colors in XIMA. Keep them out of the
          // cinematic tone-mapping pass so the preview does not wash saturated
          // red, blue, yellow and green into pale/white variants.
          next.toneMapped = false;
          if (animationId === "companion-cube" && next.map) {
            const tintedMap = createCompanionTintTexture(
              next.map,
              playerColor,
              companionTextureCache,
              ownedTextures,
            );
            if (tintedMap) {
              next.map = tintedMap;
              next.color.set(0xffffff);
            } else {
              next.color.copy(playerColor);
            }
          } else {
            next.color.copy(playerColor);
          }
        }
      }
      next.needsUpdate = true;
      ownedMaterials.push(next);
      return next;
    });
    child.material = Array.isArray(child.material) ? materials : materials[0];
  });
  return clone;
};

const seededUnit = (seed) => {
  const value = Math.sin(seed * 12.9898 + 78.233) * 43758.5453;
  return value - Math.floor(value);
};

const addLiquidAnimation = ({ model, phase, ownedMaterials, proceduralAnimators }) => {
  const liquidRoot = model.getObjectByName("XIMA_PREVIEW_ROOT") || model;
  const liquidMaterials = [];

  model.traverse((child) => {
    if (!child.isMesh) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      material.color?.lerp?.(new THREE.Color(0x72eaff), 0.28);
      material.transparent = true;
      material.opacity = Math.min(Number.isFinite(material.opacity) ? material.opacity : 1, 0.72);
      material.depthWrite = false;
      material.side = THREE.DoubleSide;
      if ("roughness" in material) material.roughness = 0.08;
      if ("metalness" in material) material.metalness = 0.02;
      if ("transmission" in material) material.transmission = Math.max(material.transmission || 0, 0.62);

      const liquidUniforms = {
        time: { value: 0 },
        phase: { value: phase },
      };
      material.onBeforeCompile = (shader) => {
        shader.uniforms.ximaLiquidTime = liquidUniforms.time;
        shader.uniforms.ximaLiquidPhase = liquidUniforms.phase;
        shader.vertexShader = `uniform float ximaLiquidTime;\nuniform float ximaLiquidPhase;\n${shader.vertexShader}`;
        shader.vertexShader = shader.vertexShader.replace(
          "#include <begin_vertex>",
          `vec3 transformed = vec3(position);
          float ximaTopMask = smoothstep(-0.18, 0.94, position.y);
          float ximaWaveA = sin(position.x * 5.2 + ximaLiquidTime * 1.85 + ximaLiquidPhase);
          float ximaWaveB = cos(position.z * 6.1 - ximaLiquidTime * 1.45 + ximaLiquidPhase * 0.7);
          transformed.y += ximaTopMask * (ximaWaveA * 0.052 + ximaWaveB * 0.036);
          transformed.x += ximaTopMask * sin(position.z * 4.3 + ximaLiquidTime * 0.9) * 0.012;
          transformed.z += ximaTopMask * cos(position.x * 4.7 - ximaLiquidTime) * 0.012;`,
        );
      };
      material.customProgramCacheKey = () => "xima-liquid-particles-v1";
      material.needsUpdate = true;
      liquidMaterials.push({ material, uniforms: liquidUniforms });
    });
  });

  const particleCount = 42;
  const positions = new Float32Array(particleCount * 3);
  const velocities = new Float32Array(particleCount * 3);
  for (let index = 0; index < particleCount; index += 1) {
    const offset = index * 3;
    positions[offset] = (seededUnit(index + phase * 17.3) - 0.5) * 1.34;
    positions[offset + 1] = -0.58 + seededUnit(index + phase * 31.7) * 1.08;
    positions[offset + 2] = (seededUnit(index + phase * 47.9) - 0.5) * 1.34;
    velocities[offset] = (seededUnit(index + phase * 59.1) - 0.5) * 0.24;
    velocities[offset + 1] = (seededUnit(index + phase * 71.3) - 0.5) * 0.18;
    velocities[offset + 2] = (seededUnit(index + phase * 83.7) - 0.5) * 0.24;
  }

  const particleGeometry = new THREE.BufferGeometry();
  particleGeometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  const particleMaterial = new THREE.PointsMaterial({
    color: 0xb7fbff,
    size: 0.095,
    transparent: true,
    opacity: 0.82,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
  });
  ownedMaterials.push(particleMaterial);
  const particles = new THREE.Points(particleGeometry, particleMaterial);
  particles.name = "XIMA liquid particles";
  particles.frustumCulled = false;
  particles.renderOrder = 6;
  liquidRoot.add(particles);

  const lowerBound = -0.72;
  const upperBound = 0.7;
  const sideBound = 0.72;
  const separationRadius = 0.17;
  proceduralAnimators.push((delta, elapsed) => {
    liquidMaterials.forEach(({ uniforms }) => {
      uniforms.time.value = elapsed;
    });

    const lateralX = Math.sin(elapsed * 0.82 + phase) * 0.42;
    const lateralZ = Math.cos(elapsed * 0.67 + phase * 0.8) * 0.34;
    for (let left = 0; left < particleCount; left += 1) {
      const leftOffset = left * 3;
      for (let right = left + 1; right < particleCount; right += 1) {
        const rightOffset = right * 3;
        const dx = positions[rightOffset] - positions[leftOffset];
        const dy = positions[rightOffset + 1] - positions[leftOffset + 1];
        const dz = positions[rightOffset + 2] - positions[leftOffset + 2];
        const distanceSquared = dx * dx + dy * dy + dz * dz;
        if (distanceSquared <= 0.000001 || distanceSquared >= separationRadius * separationRadius) continue;
        const distance = Math.sqrt(distanceSquared);
        const impulse = ((separationRadius - distance) / separationRadius) * delta * 1.8;
        const invDistance = 1 / distance;
        const impulseX = dx * invDistance * impulse;
        const impulseY = dy * invDistance * impulse;
        const impulseZ = dz * invDistance * impulse;
        velocities[leftOffset] -= impulseX;
        velocities[leftOffset + 1] -= impulseY;
        velocities[leftOffset + 2] -= impulseZ;
        velocities[rightOffset] += impulseX;
        velocities[rightOffset + 1] += impulseY;
        velocities[rightOffset + 2] += impulseZ;
      }
    }

    for (let index = 0; index < particleCount; index += 1) {
      const offset = index * 3;
      velocities[offset] += (lateralX - positions[offset] * 0.2) * delta;
      velocities[offset + 1] += (-0.62 - positions[offset + 1] * 0.08) * delta;
      velocities[offset + 2] += (lateralZ - positions[offset + 2] * 0.2) * delta;
      velocities[offset] *= 0.989;
      velocities[offset + 1] *= 0.989;
      velocities[offset + 2] *= 0.989;
      positions[offset] += velocities[offset] * delta;
      positions[offset + 1] += velocities[offset + 1] * delta;
      positions[offset + 2] += velocities[offset + 2] * delta;

      if (positions[offset] < -sideBound || positions[offset] > sideBound) {
        positions[offset] = THREE.MathUtils.clamp(positions[offset], -sideBound, sideBound);
        velocities[offset] *= -0.58;
      }
      if (positions[offset + 1] < lowerBound || positions[offset + 1] > upperBound) {
        positions[offset + 1] = THREE.MathUtils.clamp(positions[offset + 1], lowerBound, upperBound);
        velocities[offset + 1] *= -0.42;
      }
      if (positions[offset + 2] < -sideBound || positions[offset + 2] > sideBound) {
        positions[offset + 2] = THREE.MathUtils.clamp(positions[offset + 2], -sideBound, sideBound);
        velocities[offset + 2] *= -0.58;
      }
    }
    particleGeometry.attributes.position.needsUpdate = true;
  });
};

const addVoxelAnimation = ({ model, phase, proceduralAnimators }) => {
  const shell = model.getObjectByName("VoxelShell");
  const core = model.getObjectByName("VoxelCore");
  if (!shell || !core) return;

  const baseShellQuaternion = shell.quaternion.clone();
  const baseShellScale = shell.scale.clone();
  const baseCoreScale = core.scale.clone();
  const spinQuaternion = new THREE.Quaternion();
  const tiltQuaternion = new THREE.Quaternion();
  const yAxis = new THREE.Vector3(0, 1, 0);
  const xAxis = new THREE.Vector3(1, 0, 0);
  const direction = Math.floor(phase) % 2 === 0 ? 1 : -1;
  const coreMaterials = [];
  core.traverse((child) => {
    if (!child.isMesh) return;
    const materials = Array.isArray(child.material) ? child.material : [child.material];
    materials.forEach((material) => {
      if (material.emissive) {
        material.emissive.set(0x1de5ff);
        coreMaterials.push(material);
      }
    });
  });

  proceduralAnimators.push((delta, elapsed) => {
    const pulse = 1 + Math.sin(elapsed * 2.35 + phase) * 0.13;
    const shellPulse = 1 + Math.sin(elapsed * 1.45 + phase * 0.6) * 0.035;
    spinQuaternion.setFromAxisAngle(yAxis, direction * elapsed * 0.72);
    tiltQuaternion.setFromAxisAngle(xAxis, Math.sin(elapsed * 0.83 + phase) * 0.12);
    shell.quaternion.copy(baseShellQuaternion).multiply(spinQuaternion).multiply(tiltQuaternion);
    shell.scale.copy(baseShellScale).multiplyScalar(shellPulse);
    core.scale.copy(baseCoreScale).multiplyScalar(pulse);
    coreMaterials.forEach((material) => {
      material.emissiveIntensity = 1.15 + pulse * 0.85;
    });
  });
};

const addProceduralAnimation = ({ model, animationId, phase, ownedMaterials, proceduralAnimators }) => {
  if (animationId === "liquid-glass-cube") {
    addLiquidAnimation({ model, phase, ownedMaterials, proceduralAnimators });
  } else if (animationId === "voxel-core-cube") {
    addVoxelAnimation({ model, phase, proceduralAnimators });
  }
};

const DesignModePreview = ({
  boardTheme,
  wallTexture,
  background,
  blockMaterial,
  blockAnimation,
  commonAsset,
  specialAsset,
  commonBlockId,
  specialBlockId,
}) => {
  const mountRef = useRef(null);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return undefined;

    let disposed = false;
    let frame = 0;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color("#020814");

    const camera = new THREE.PerspectiveCamera(34, 1, 0.1, 100);
    camera.position.set(14.5, 11, 17.5);

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.12;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFShadowMap;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.domElement.className = "xima-design-preview-canvas";
    mount.appendChild(renderer.domElement);

    const environmentGenerator = new THREE.PMREMGenerator(renderer);
    const roomEnvironment = new RoomEnvironment();
    const environmentTarget = environmentGenerator.fromScene(roomEnvironment, 0.04);
    scene.environment = environmentTarget.texture;

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.075;
    controls.enablePan = false;
    controls.minDistance = 16;
    controls.maxDistance = 42;
    controls.minPolarAngle = 0.35;
    controls.maxPolarAngle = 1.45;
    controls.target.set(0, 1.5, 0);

    scene.add(new THREE.HemisphereLight(0xbcecff, 0x101622, 2.25));
    const keyLight = new THREE.DirectionalLight(0xffffff, 3.4);
    keyLight.position.set(8, 16, 11);
    keyLight.castShadow = true;
    keyLight.shadow.mapSize.set(2048, 2048);
    scene.add(keyLight);
    const rimLight = new THREE.DirectionalLight(0x39cfff, 2.1);
    rimLight.position.set(-12, 7, -9);
    scene.add(rimLight);

    const starGeometry = new THREE.BufferGeometry();
    const starPositions = [];
    for (let index = 0; index < 750; index += 1) {
      const radius = 27 + (index % 17) * 0.7;
      const theta = index * 2.399963229728653;
      const vertical = -0.72 + ((index * 37) % 145) / 100;
      const horizontalRadius = Math.sqrt(Math.max(0, 1 - vertical * vertical));
      starPositions.push(
        Math.cos(theta) * horizontalRadius * radius,
        vertical * radius,
        Math.sin(theta) * horizontalRadius * radius,
      );
    }
    starGeometry.setAttribute("position", new THREE.Float32BufferAttribute(starPositions, 3));
    scene.add(new THREE.Points(starGeometry, new THREE.PointsMaterial({
      color: 0xbce6ff,
      size: 0.075,
      transparent: true,
      opacity: 0.72,
      depthWrite: false,
    })));

    const textureLoader = new THREE.TextureLoader();
    textureLoader.setCrossOrigin("anonymous");
    const gltfLoader = new GLTFLoader();
    const ownedTextures = [];
    const ownedMaterials = [];
    const companionTextureCache = new Map();
    const mixers = [];
    const gearAnimationCycles = [];
    const proceduralAnimators = [];

    const setupScene = async () => {
      const blockTextureRepeat = resolveBlockTextureRepeat(blockMaterial?.id);
      const wallBase = wallTexture?.id === "default"
        ? Promise.resolve(null)
        : loadTexture(textureLoader, wallTexture?.previewTexture, { repeat: wallTexture?.isFlag ? 1 : 4 });
      const wallNormal = wallTexture?.id === "default"
        ? Promise.resolve(null)
        : loadTexture(textureLoader, wallTexture?.previewNormal, { colorSpace: false, repeat: 4 });
      const blockBase = blockMaterial?.id === "default"
        ? loadTexture(textureLoader, commonBlockId !== "default_star" ? commonAsset : "")
        : loadTexture(textureLoader, blockMaterial?.previewTexture, { repeat: blockTextureRepeat });
      const blockNormal = blockMaterial?.id === "default"
        ? Promise.resolve(null)
        : loadTexture(textureLoader, blockMaterial?.previewNormal, { colorSpace: false, repeat: blockTextureRepeat });
      const blockRoughness = blockMaterial?.id === "default"
        ? Promise.resolve(null)
        : loadTexture(textureLoader, blockMaterial?.previewRoughness, { colorSpace: false, repeat: blockTextureRepeat });
      const blockMetalness = blockMaterial?.id === "default"
        ? Promise.resolve(null)
        : loadTexture(textureLoader, blockMaterial?.previewMetalness, { colorSpace: false, repeat: blockTextureRepeat });
      const animationPromise = blockAnimation?.id !== "default" && blockAnimation?.model
        ? gltfLoader.loadAsync(blockAnimation.model).catch(() => null)
        : Promise.resolve(null);

      const [
        backgroundTexture,
        selectedWallTexture,
        selectedWallNormal,
        commonTexture,
        commonNormal,
        commonRoughness,
        commonMetalness,
        specialTexture,
        boardGltf,
        animationGltf,
      ] = await Promise.all([
        loadTexture(textureLoader, background?.image),
        wallBase,
        wallNormal,
        blockBase,
        blockNormal,
        blockRoughness,
        blockMetalness,
        loadTexture(textureLoader, specialAsset),
        gltfLoader.loadAsync(BOARD_MODEL_URL).catch(() => null),
        animationPromise,
      ]);
      if (disposed) return;

      [
        backgroundTexture,
        selectedWallTexture,
        selectedWallNormal,
        commonTexture,
        commonNormal,
        commonRoughness,
        commonMetalness,
        specialTexture,
      ].forEach((texture) => {
        if (texture) ownedTextures.push(texture);
      });
      if (backgroundTexture) scene.background = backgroundTexture;
      else if (background?.plainColor) scene.background = new THREE.Color(background.plainColor);

      if (boardGltf?.scene) {
        const board = boardGltf.scene;
        board.name = "TABLERO ESPEJO SELECCIONADO";
        // The FBX master stores its board plane in XY. The exported GLB keeps
        // that authored orientation, so rotate only the preview instance into
        // the same Y-up gameplay orientation used by Unity.
        board.rotation.x = Math.PI / 2;
        board.updateMatrixWorld(true);
        board.traverse((child) => {
          if (!child.isMesh) return;
          child.castShadow = true;
          child.receiveShadow = true;
          const materialName = normalizeMaterialName(child.material?.name);
          let material;
          if (materialName.includes("casillas oscuras")) {
            material = new THREE.MeshPhysicalMaterial({
              name: "casillas oscuras",
              color: new THREE.Color(boardTheme?.dark || "#005c4a"),
              metalness: 0.86,
              roughness: 0.08,
              clearcoat: 1,
              clearcoatRoughness: 0.04,
              side: THREE.DoubleSide,
            });
          } else if (materialName.includes("casillas claras")) {
            material = new THREE.MeshStandardMaterial({
              name: "casillas claras",
              color: new THREE.Color("#fff7f7"),
              metalness: 0.03,
              roughness: 0.62,
              side: THREE.DoubleSide,
            });
          } else if (materialName.includes("paredes")) {
            material = new THREE.MeshPhysicalMaterial({
              name: "paredes",
              color: selectedWallTexture ? 0xffffff : 0xb9eff7,
              map: selectedWallTexture,
              normalMap: selectedWallNormal,
              metalness: selectedWallTexture ? 0.12 : 0.02,
              roughness: selectedWallTexture ? 0.36 : 0.08,
              transmission: selectedWallTexture ? 0 : 0.76,
              transparent: !selectedWallTexture,
              opacity: selectedWallTexture ? 1 : 0.32,
              thickness: 0.3,
              ior: 1.45,
              side: THREE.DoubleSide,
            });
          } else {
            material = new THREE.MeshPhysicalMaterial({
              name: "cantos",
              color: 0x77c9ed,
              metalness: 0.72,
              roughness: 0.1,
              clearcoat: 1,
              clearcoatRoughness: 0.05,
              side: THREE.DoubleSide,
            });
          }
          ownedMaterials.push(material);
          child.material = material;
          if (wallTexture?.isFlag && selectedWallTexture && materialName.includes("paredes")) {
            applyFlagWallUvs(child);
          }
        });
        scene.add(board);
        mount.dataset.boardLoaded = "true";
        const boardBounds = new THREE.Box3().setFromObject(board);
        mount.dataset.boardBounds = [
          boardBounds.min.x,
          boardBounds.min.y,
          boardBounds.min.z,
          boardBounds.max.x,
          boardBounds.max.y,
          boardBounds.max.z,
        ].map((value) => value.toFixed(2)).join(",");
      } else {
        mount.dataset.boardLoaded = "false";
      }

      if (animationGltf?.scene) {
        BLOCK_POSITIONS.forEach((position, index) => {
          const model = cloneAnimationModel(
            animationGltf.scene,
            position,
            blockAnimation.id,
            ownedMaterials,
            index,
            companionTextureCache,
            ownedTextures,
          );
          model.rotation.y = (index % 4) * Math.PI * 0.5;
          scene.add(model);
          if (animationGltf.animations?.length && MIXER_MODEL_ANIMATIONS.has(blockAnimation.id)) {
            const mixer = new THREE.AnimationMixer(model);
            const actions = animationGltf.animations.map((clip) => mixer.clipAction(clip));
            if (blockAnimation.id === "gear-cube") {
              actions.forEach((action) => {
                action.setLoop(THREE.LoopOnce, 1);
                action.clampWhenFinished = true;
                action.play();
              });
              gearAnimationCycles.push({
                mixer,
                actions,
                duration: Math.max(...animationGltf.animations.map((clip) => clip.duration)),
                elapsed: 0,
                holdRemaining: 0,
              });
            } else {
              actions.forEach((action) => action.play());
              mixers.push(mixer);
            }
          }
          if (PROCEDURAL_MODEL_ANIMATIONS.has(blockAnimation.id)) {
            addProceduralAnimation({
              model,
              animationId: blockAnimation.id,
              phase: index * 0.73,
              ownedMaterials,
              proceduralAnimators,
            });
          }
        });
        const specialModel = cloneAnimationModel(
          animationGltf.scene,
          SPECIAL_POSITION,
          blockAnimation.id,
          ownedMaterials,
          BLOCK_POSITIONS.length,
          companionTextureCache,
          ownedTextures,
        );
        specialModel.name = `${blockAnimation.id} special preview`;
        scene.add(specialModel);
        if (animationGltf.animations?.length && MIXER_MODEL_ANIMATIONS.has(blockAnimation.id)) {
          const specialMixer = new THREE.AnimationMixer(specialModel);
          const specialActions = animationGltf.animations.map((clip) => specialMixer.clipAction(clip));
          if (blockAnimation.id === "gear-cube") {
            specialActions.forEach((action) => {
              action.setLoop(THREE.LoopOnce, 1);
              action.clampWhenFinished = true;
              action.play();
            });
            gearAnimationCycles.push({
              mixer: specialMixer,
              actions: specialActions,
              duration: Math.max(...animationGltf.animations.map((clip) => clip.duration)),
              elapsed: 0,
              holdRemaining: 0,
            });
          } else {
            specialActions.forEach((action) => action.play());
            mixers.push(specialMixer);
          }
        }
        if (PROCEDURAL_MODEL_ANIMATIONS.has(blockAnimation.id)) {
          addProceduralAnimation({
            model: specialModel,
            animationId: blockAnimation.id,
            phase: BLOCK_POSITIONS.length * 0.73,
            ownedMaterials,
            proceduralAnimators,
          });
        }
        mount.dataset.animationModelLoaded = blockAnimation.id;
        mount.dataset.animationMotion = MIXER_MODEL_ANIMATIONS.has(blockAnimation.id)
          || PROCEDURAL_MODEL_ANIMATIONS.has(blockAnimation.id)
          ? "active"
          : "static";
      } else {
        const commonColor = blockMaterial?.id === "default" ? 0x079fd9 : 0xffffff;
        BLOCK_POSITIONS.forEach((position) => {
          const cube = addPreviewCube({
            scene,
            position,
            color: commonColor,
            texture: commonTexture,
            normalMap: commonNormal,
            roughnessMap: commonRoughness,
            metalnessMap: commonMetalness,
          });
          cube.material.forEach((material) => ownedMaterials.push(material));
        });
        const specialCube = addPreviewCube({
          scene,
          position: SPECIAL_POSITION,
          size: 0.94,
          color: commonColor,
          texture: commonTexture,
          normalMap: commonNormal,
          roughnessMap: commonRoughness,
          metalnessMap: commonMetalness,
          special: true,
        });
        specialCube.material.forEach((material) => ownedMaterials.push(material));
        mount.dataset.animationModelLoaded = blockAnimation?.id === "default" ? "default" : "fallback";
        mount.dataset.animationMotion = "static";
      }
      addSpecialMarkers(scene, SPECIAL_POSITION, 0.94, specialTexture, ownedMaterials);
    };

    setupScene();

    const resize = () => {
      const bounds = mount.getBoundingClientRect();
      const width = Math.max(320, Math.round(bounds.width));
      const height = Math.max(420, Math.round(bounds.height));
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height, false);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(mount);
    resize();

    const clock = new THREE.Clock();
    let animationElapsed = 0;
    const animate = () => {
      if (disposed) return;
      const delta = Math.min(clock.getDelta(), 0.05);
      animationElapsed += delta;
      controls.update();
      mixers.forEach((mixer) => mixer.update(delta * 0.55));
      gearAnimationCycles.forEach((cycle) => {
        if (cycle.holdRemaining > 0) {
          cycle.holdRemaining = Math.max(0, cycle.holdRemaining - delta);
          if (cycle.holdRemaining === 0) {
            cycle.mixer.stopAllAction();
            cycle.mixer.time = 0;
            cycle.elapsed = 0;
            cycle.actions.forEach((action) => action.reset().play());
          }
          return;
        }

        const animationDelta = delta * GEAR_ANIMATION_SPEED;
        const remaining = Math.max(0, cycle.duration - cycle.elapsed);
        const appliedDelta = Math.min(animationDelta, remaining);
        cycle.mixer.update(appliedDelta);
        cycle.elapsed += appliedDelta;
        if (cycle.elapsed >= cycle.duration - 0.0001) {
          cycle.mixer.setTime(cycle.duration);
          cycle.elapsed = cycle.duration;
          cycle.holdRemaining = GEAR_SQUARED_HOLD_SECONDS;
        }
      });
      proceduralAnimators.forEach((updateAnimation) => updateAnimation(delta, animationElapsed));
      renderer.render(scene, camera);
      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);

    return () => {
      disposed = true;
      cancelAnimationFrame(frame);
      observer.disconnect();
      controls.dispose();
      mixers.forEach((mixer) => mixer.stopAllAction());
      gearAnimationCycles.forEach((cycle) => cycle.mixer.stopAllAction());
      scene.traverse((child) => child.geometry?.dispose?.());
      ownedMaterials.forEach((material) => material.dispose());
      ownedTextures.forEach((texture) => texture.dispose());
      environmentTarget.dispose();
      environmentGenerator.dispose();
      roomEnvironment.dispose?.();
      renderer.dispose();
      renderer.domElement.remove();
    };
  }, [background, blockAnimation, blockMaterial, boardTheme, commonAsset, commonBlockId, specialAsset, specialBlockId, wallTexture]);

  return (
    <div
      ref={mountRef}
      className="xima-design-preview-canvas-wrap"
      role="application"
      aria-label="Vista previa 3D interactiva del tablero espejo y los bloques"
      data-board-model="TABLERO ESPEJO SELECCIONADO"
    >
      <div className="xima-design-preview-badges" aria-hidden="true">
        <span>Tablero espejo real</span>
        <span>{boardTheme?.label}</span>
        <span>{wallTexture?.label}</span>
        <span>{blockAnimation?.label || blockMaterial?.label}</span>
      </div>
      <div className="xima-design-preview-help" aria-hidden="true">
        Arrastra para girar · Rueda para acercar
      </div>
    </div>
  );
};

export default DesignModePreview;
